import find from 'lodash/find';
import map from 'lodash/map';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconCheck from '@spothero/icons/check';
import IconExclamationCircle from '@spothero/icons/exclamation-circle';
import Alert from '@spothero/ui/alert';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import Loader from '@spothero/ui/loader';
import Radio from '@spothero/ui/radio';
import RadioGroup from '@spothero/ui/radio-group';
import Tooltip from '@spothero/ui/tooltip';
import Price from 'common/price/Price';
import APIUtils from '@spothero/utils/api';
import withConfig from 'config/withConfig';
import OversizedFeeAPI from 'api/oversized-fee';
import AddNewVehicleForm from './AddNewVehicleForm';

class VehicleList extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        user: PropTypes.object,
        facilityId: PropTypes.number,
        vehicleProfileId: PropTypes.number,
        edit: PropTypes.bool,
        isParentVisible: PropTypes.bool,
        required: PropTypes.bool,
        checkOversized: PropTypes.bool,
        feeMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        defaultFeeMessage: PropTypes.string,
        iDontKnowChecked: PropTypes.bool,
        vehicleNotAllowed: PropTypes.bool,
        showUnknownChoices: PropTypes.bool,
        showSaveAsDefault: PropTypes.bool,
        licensePlateRequired: PropTypes.bool,
        loadVehicleDataOnMount: PropTypes.bool,
        newVehicleFormErrors: PropTypes.array,
        onOversizedFeeUpdated: PropTypes.func,
        onIDontKnowChanged: PropTypes.func,
        onShowAddNew: PropTypes.func,
        onHideAddNew: PropTypes.func,
        currencyType: PropTypes.string,
        supportsOversizeFeeType: PropTypes.bool,
        oversizeFee: PropTypes.object,
        onVehicleChange: PropTypes.func,
        isLoadingRates: PropTypes.bool,
    };
    static defaultProps = {
        edit: true,
        required: true,
        defaultFeeMessage: 'Oversized fees',
        showUnknownChoices: true,
        showSaveAsDefault: true,
    };

    constructor(props) {
        super(props);

        const {
            iDontKnowChecked,
            feeMessage,
            defaultFeeMessage,
            vehicleNotAllowed,
            user,
            showUnknownChoices,
            vehicleProfileId,
        } = props;
        const {
            vehicleProfileId: newVehicleProfileId,
            vehicles,
            showAddNew,
            vehicleMakeModelPlate,
        } = this._getUserVehicles({
            user,
            showUnknownChoices,
            iDontKnowChecked,
            vehicleProfileId,
        });

        this._requiredErrorMessage =
            'Please select or add a vehicle and license plate.';

        this.state = {
            vehicles,
            vehicleProfileId: newVehicleProfileId,
            vehicleMakeModelPlate,
            iDontKnowChecked,
            feeMessage,
            requiredError: null,
            showWarningTooltip: false,
            highlight: false,
            fee: null,
            feeError: null,
            apiFailed: false,
            feeMessageLoading: false,
            defaultFeeMessage,
            vehicleNotAllowed,
            showAddNew,
            isNewVehicleValid: false,
        };
    }

    componentDidMount() {
        const {loadVehicleDataOnMount, checkOversized} = this.props;

        if (loadVehicleDataOnMount) {
            const {vehicleProfileId} = this.state;

            if (!vehicleProfileId) {
                return;
            }

            if (checkOversized) {
                this._loadOversizedFee({vehicleProfileId});
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {showAddNew} = this.state;
        const {showAddNew: prevShowAddNew} = prevState;

        if (prevShowAddNew && !showAddNew) {
            const {onHideAddNew} = this.props;

            if (onHideAddNew) {
                onHideAddNew();
            }
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;

        if (this._feeLoader) {
            this._feeLoader.cancel();
        }
    }

    _onWarningTooltipHidden = () => {
        this.setState({
            showWarningTooltip: false,
        });
    };

    _onVehicleChange = evt => {
        const {
            onShowAddNew,
            checkOversized,
            onVehicleChange,
            user,
        } = this.props;
        const {vehicles} = this.state;
        const value = evt.target.value;
        const selectedVehicle = find(vehicles, {value});
        // updated list of vehicles with the correct one checked
        const updatedVehicles = this._updateCheckedVehicle(value);

        this.setState({
            requiredError: null,
        });

        if (value === 'DONTKNOW') {
            this._onIDontKnowSelected();

            this.setState({
                vehicles: updatedVehicles,
            });
        } else if (value === 'new') {
            this.setState({
                vehicleProfileId: null,
                vehicleInfoId: null,
                iDontKnowChecked: false,
                fee: null,
                feeMessage: null,
                feeError: null,
                showAddNew: true,
                showWarningTooltip: false,
                isNewVehicleValid: false,
                vehicleMakeModelPlate: null,
            });

            if (onShowAddNew) {
                onShowAddNew();
            }
        } else if (value === 'unlisted') {
            this.setState({
                vehicles: updatedVehicles,
                vehicleProfileId: null,
                vehicleInfoId: null,
                iDontKnowChecked: false,
                fee: null,
                feeMessage: null,
                feeError: null,
                showAddNew: false,
                showWarningTooltip: false,
                isNewVehicleValid: false,
                vehicleMakeModelPlate: selectedVehicle.label,
                selectedLicensePlate: selectedVehicle.licensePlateValue,
                unlistedModel: true,
            });
        } else if (checkOversized) {
            const vehicleProfileId = Number(selectedVehicle.value);

            this._loadOversizedFee({vehicleProfileId});

            this.setState({
                vehicles: updatedVehicles,
            });
        } else {
            const newState = {
                vehicles: updatedVehicles,
                iDontKnowChecked: false,
                showAddNew: false,
                isNewVehicleValid: false,
                showWarningTooltip: false,
                vehicleMakeModelPlate: selectedVehicle.label,
            };

            if (user) {
                newState.vehicleProfileId = Number(value);
            } else {
                newState.vehicleInfoId = Number(value);
            }

            this.setState(newState);
        }

        if (onVehicleChange) {
            onVehicleChange(selectedVehicle);
        }
    };

    _onIDontKnowSelected() {
        const {
            onOversizedFeeUpdated,
            checkOversized,
            onIDontKnowChanged,
        } = this.props;

        const newState = {
            vehicleProfileId: null,
            vehicleInfoId: null,
            selectedLicensePlate: 'DONTKNOW',
            unlistedModel: null,
            vehicleMakeModelPlate: `I don't know my vehicle details right now`,
            iDontKnowChecked: true,
            fee: null,
            feeMessage: null,
            feeError: null,
            requiredError: null,
            highlight: false,
            showAddNew: false,
            isNewVehicleValid: false,
            showWarningTooltip: true,
        };

        this.setState(newState);

        if (onIDontKnowChanged) {
            onIDontKnowChanged(true);
        }

        if (checkOversized && onOversizedFeeUpdated) {
            onOversizedFeeUpdated({
                response: false,
                state: newState,
            });
        }
    }

    _updateCheckedVehicle = value => {
        const updatedVehicles = this.state.vehicles.map((vehicle, i) => {
            const checked = value === vehicle.value;

            return {
                ...vehicle,
                checked,
            };
        });

        return updatedVehicles;
    };

    _getUserVehicles({
        user,
        showUnknownChoices,
        iDontKnowChecked,
        vehicleProfileId,
    }) {
        const showAddNew = !(
            (user.vehicles && user.vehicles.length > 0) ||
            iDontKnowChecked
        );
        const vehicles = user.vehicles ? user.vehicles : [];
        let newVehicleProfileId = vehicleProfileId;
        let vehicleMakeModelPlate;

        const initialVehicles = map(vehicles, vehicle => {
            if (!newVehicleProfileId && vehicle.isDefault) {
                newVehicleProfileId = vehicle.vehicleProfileId;
            }
            const makeModel = vehicle.makeLabel
                ? `${vehicle.makeLabel} ${vehicle.modelLabel}`
                : 'No Vehicle';
            const licensePlate = vehicle.licensePlateValue
                ? vehicle.licensePlateValue
                : 'No License Plate';

            if (newVehicleProfileId === vehicle.vehicleProfileId) {
                vehicleMakeModelPlate = `${makeModel} | ${licensePlate}`;
            }

            return {
                label: `${makeModel} | ${licensePlate}`,
                licensePlateValue: vehicle.licensePlateValue,
                value: vehicle.vehicleProfileId
                    ? vehicle.vehicleProfileId.toString()
                    : null,
                checked:
                    !showAddNew &&
                    newVehicleProfileId === vehicle.vehicleProfileId,
            };
        });

        if (showUnknownChoices) {
            initialVehicles.push({
                label: `I don't know my vehicle details right now`,
                value: 'DONTKNOW',
                checked: iDontKnowChecked,
                className: 'VehicleList-dont-know',
            });
        }

        initialVehicles.push({
            label: 'Add new vehicle',
            value: 'new',
            checked: showAddNew,
        });

        if (filter(initialVehicles, {checked: true}).length === 0) {
            // there are no checked vehicles, this may happen if the user deleted their vehicle in their account and its
            // still showing up on parking pass or similar so we select the first one available
            initialVehicles[0].checked = true;
            newVehicleProfileId = initialVehicles[0].value;
        }

        return {
            vehicleProfileId: newVehicleProfileId,
            vehicles: initialVehicles,
            vehicleMakeModelPlate,
            showAddNew,
        };
    }

    _loadOversizedFee({vehicleProfileId}, hideShowNew = true) {
        const {
            facilityId,
            user: {id: userId},
        } = this.props;
        const newState = {
            feeMessageLoading: false,
        };

        this.setState({
            feeMessageLoading: true,
            feeError: null,
            showAddNew: !hideShowNew,
            showWarningTooltip: false,
            requiredError: null,
        });

        this._feeLoader = APIUtils.makeCancelable(
            OversizedFeeAPI.getOversizedFee({
                facilityId,
                vehicleProfileId,
                userId,
            })
        );
        this._feeLoader.promise
            .then(response => {
                const {
                    fee,
                    message,
                    not_allowed: notAllowed,
                } = response.data.data.results;

                newState.fee = fee ? fee : 0;
                newState.feeMessage = message
                    ? message
                    : 'No oversized fees apply for this vehicle.';
                newState.vehicleNotAllowed = notAllowed;

                this._oversizedFeeLoaded(response, newState);
            })
            .catch(response => {
                newState.feeError =
                    'We are unable to check for oversized fees at this time. Please continue to checkout and update your vehicle information before you park.';

                this._oversizedFeeLoaded(response, newState);
            });
    }

    _oversizedFeeLoaded(response, newState) {
        if (this._isUnmounted) {
            return;
        }

        const {onOversizedFeeUpdated} = this.props;

        this.setState(newState);

        if (onOversizedFeeUpdated) {
            onOversizedFeeUpdated({
                response,
                state: this.state,
            });
        }
    }

    _setFeeError = feeError => {
        this.setState({
            feeError,
            apiFailed: true,
        });
    };

    _renderDontKnowTooltip() {
        const {isParentVisible, isMobile, licensePlateRequired} = this.props;
        const {showWarningTooltip} = this.state;

        if (!isParentVisible || !showWarningTooltip || isMobile) {
            return null;
        }

        let toolTipMessage;

        if (licensePlateRequired) {
            toolTipMessage = `Visit our app or "My Account" page to update
            your license plate and vehicle before you park so you don't
            get towed or fined.`;
        } else {
            toolTipMessage = `That's alright! Knowing your vehicle details before
            entering the garage can help with conversations about the
            oversize fees at the garage.`;
        }

        return (
            <Tooltip
                className="VehicleList-dont-know-tooltip"
                position="right"
                triggerSelector=".VehicleList-dont-know"
                tipOffset={10}
                onHidden={this._onWarningTooltipHidden}
            >
                {toolTipMessage}
            </Tooltip>
        );
    }

    _renderOversizedFeeMessage() {
        const {feeMessage, defaultFeeMessage, vehicleNotAllowed} = this.state;

        if (feeMessage && !isEmpty(feeMessage)) {
            return (
                <div className="content">
                    <strong>
                        {feeMessage}
                        {!vehicleNotAllowed &&
                            feeMessage !==
                                'No oversized fees apply for this vehicle.' &&
                            ' due to the garage.'}
                    </strong>
                </div>
            );
        } else {
            return (
                <div className="content">
                    <strong>Vehicle not found</strong>{' '}
                    <small>
                        {defaultFeeMessage.replace(/<br>/g, '. ')} may be
                        charged at the spot.
                    </small>
                </div>
            );
        }
    }

    _renderFeeMessageLoader() {
        const {feeMessageLoading, isLoadingRates} = this.state;

        if (!feeMessageLoading && !isLoadingRates) {
            return null;
        }

        return (
            <div className="VehicleList-message">
                <Loader size={20} borderWidth={2} />
            </div>
        );
    }

    _renderOversizedFee() {
        const {
            fee,
            feeMessageLoading,
            vehicleNotAllowed,
            feeError,
        } = this.state;

        return (
            <Fragment>
                {!fee && feeError && !feeMessageLoading && <p>{feeError}</p>}
                {(fee || fee === 0) && !feeMessageLoading && (
                    <div className="VehicleList-message">
                        <Alert
                            size="lg"
                            type={vehicleNotAllowed ? 'danger' : 'muted'}
                        >
                            {this._renderOversizedFeeMessage()}
                        </Alert>
                    </div>
                )}
            </Fragment>
        );
    }

    _renderIncludedOversizedFee() {
        const {oversizeFee, currencyType} = this.props;
        const {vehicleProfileId, isNewVehicleValid} = this.state;
        const noOversizeFeesApply =
            (isNewVehicleValid || vehicleProfileId) && !oversizeFee;
        const classes = classNames('VehicleList-oversize-message', {
            'VehicleList-oversize-message-no-fee': noOversizeFeesApply,
        });

        return (
            <div className={classes}>
                {noOversizeFeesApply ? (
                    <IconCheck className="Icon-check" />
                ) : (
                    <IconExclamationCircle />
                )}
                {(vehicleProfileId || isNewVehicleValid) && oversizeFee && (
                    <p>
                        Your vehicle has at least one oversize dimension. An
                        oversize vehicle fee of{' '}
                        <Price
                            amount={oversizeFee.price}
                            currencyType={currencyType}
                        />{' '}
                        will be included in your price.
                    </p>
                )}
                {!vehicleProfileId && !isNewVehicleValid && (
                    <p>
                        This facility charges oversize vehicle fees. You could
                        be charged an oversize fee on site if you are driving a
                        larger vehicle. Please select your vehicle above to
                        check oversize fees.
                    </p>
                )}
                {noOversizeFeesApply && (
                    <p>No oversized fees apply for this vehicle.</p>
                )}
            </div>
        );
    }

    _renderVehicleList() {
        const {
            isMobile,
            user,
            required,
            newVehicleFormErrors,
            checkOversized,
            showSaveAsDefault,
            supportsOversizeFeeType,
            isLoadingRates,
            oversizeFee,
            licensePlateRequired,
            edit,
        } = this.props;
        const {showAddNew, vehicles, vehicleProfileId} = this.state;

        if (edit) {
            return (
                <Fragment>
                    {vehicles.length > 1 && (
                        <Fragment>
                            <Label>Vehicle</Label>
                            {checkOversized && (
                                <Alert type="highlight">
                                    This spot may charge fees onsite for
                                    oversized vehicles. Enter vehicle details to
                                    see estimated fees.
                                </Alert>
                            )}
                            {supportsOversizeFeeType && (
                                <Alert type="highlight">
                                    Avoid surprise oversize charges. Add your
                                    vehicle to pay for oversize vehicles in
                                    advance.
                                </Alert>
                            )}
                            {!supportsOversizeFeeType &&
                                !checkOversized &&
                                licensePlateRequired && (
                                    <Alert type="highlight">
                                        Don&apos;t get towed. Enter your vehicle
                                        details before you park.
                                    </Alert>
                                )}
                            <FormGroup>
                                <RadioGroup
                                    id="VehicleList-vehicle-options"
                                    name="vehicle"
                                    required={required}
                                    error={
                                        <FormElementError>
                                            {this._requiredErrorMessage}
                                        </FormElementError>
                                    }
                                    onChange={this._onVehicleChange}
                                >
                                    {map(vehicles, (vehicle, i) => {
                                        return (
                                            <Radio
                                                key={vehicle.value}
                                                label={
                                                    <Label
                                                        className={
                                                            vehicle.className
                                                        }
                                                    >
                                                        {vehicle.label}
                                                    </Label>
                                                }
                                                value={vehicle.value}
                                                defaultChecked={vehicle.checked}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormGroup>
                        </Fragment>
                    )}
                    {showAddNew && (
                        <FormGroup>
                            <AddNewVehicleForm
                                ref={node => {
                                    this._newVehicleForm = node;
                                }}
                                user={user}
                                formErrors={newVehicleFormErrors}
                                setOversizeFeeError={this._setFeeError}
                                showSaveAsDefault={showSaveAsDefault}
                                useVehicleSelectLabels={!isMobile}
                            />
                        </FormGroup>
                    )}
                    {supportsOversizeFeeType &&
                        (oversizeFee || !vehicleProfileId) &&
                        !isLoadingRates &&
                        this._renderIncludedOversizedFee()}
                    {checkOversized && this._renderOversizedFee()}
                    {(checkOversized || isLoadingRates) &&
                        this._renderFeeMessageLoader()}
                    {!supportsOversizeFeeType && this._renderDontKnowTooltip()}
                </Fragment>
            );
        }
    }

    async submit() {
        const {onIDontKnowChanged, onVehicleChange} = this.props;
        const {
            showAddNew,
            iDontKnowChecked,
            vehicleProfileId,
            vehicleInfoId,
            unlistedModel,
            selectedLicensePlate,
            apiFailed,
            vehicleMakeModelPlate,
        } = this.state;

        if (apiFailed) {
            return true;
        }

        let newFormData = null;
        let newVehicleProfileId = vehicleProfileId;
        let newVehicleInfoId = vehicleInfoId;
        let newUnlistedModel = unlistedModel;
        let newSelectedLicensePlate = selectedLicensePlate;
        let newVehicleMakeModelPlate = vehicleMakeModelPlate;

        if (showAddNew && this._newVehicleForm) {
            newFormData = await this._newVehicleForm.submit();

            if (!newFormData) {
                if (onIDontKnowChanged) {
                    onIDontKnowChanged(iDontKnowChecked);
                }

                return false;
            }

            newVehicleProfileId = parseInt(newFormData.vehicleProfileId, 10);
            newVehicleInfoId = parseInt(newFormData.vehicleInfoId, 10);
            newUnlistedModel = Boolean(newFormData.unlistedModel);
            newSelectedLicensePlate = newFormData.licensePlateValue;
            newVehicleMakeModelPlate = newFormData.vehicleMakeModelPlate;

            const {licensePlateValue} = newFormData;

            const vehicleToAdd = {
                checked: true,
                licensePlateValue,
                value: newFormData.vehicleProfileId
                    ? String(newFormData.vehicleProfileId)
                    : newFormData.vehicleInfoId
                    ? String(newFormData.vehicleInfoId)
                    : newFormData.unlistedModel
                    ? 'unlisted'
                    : null,
                label: newFormData.vehicleMakeModelPlate,
            };

            // make sure all vehicles are unchecked before adding new vehicle
            const unselectedVehicles = this.state.vehicles.map((vehicle, i) => {
                return {
                    ...vehicle,
                    checked: false,
                };
            });

            const newState = {
                vehicles: [vehicleToAdd, ...unselectedVehicles],
                showAddNew: false,
                vehicleMakeModelPlate: newVehicleMakeModelPlate,
            };

            if (newVehicleProfileId) {
                newState.vehicleProfileId = newVehicleProfileId;
            } else {
                newState.selectedLicensePlate = licensePlateValue;

                if (newVehicleInfoId) {
                    newState.vehicleInfoId = parseInt(newVehicleInfoId, 10);
                } else if (unlistedModel) {
                    newState.unlistedModel = Boolean(unlistedModel);
                }
            }

            this.setState(newState);

            if (onVehicleChange) {
                onVehicleChange(vehicleToAdd);
            }
        } else if (iDontKnowChecked) {
            newVehicleProfileId = null;
            newVehicleInfoId = null;
            newUnlistedModel = null;
            newSelectedLicensePlate = 'DONTKNOW';
            newVehicleMakeModelPlate = `I don't know my vehicle details right now`;

            this.setState({
                vehicleProfileId: newVehicleProfileId,
                vehicleInfoId: newVehicleInfoId,
                selectedLicensePlate: newSelectedLicensePlate,
                unlistedModel: newUnlistedModel,
                vehicleMakeModelPlate: newVehicleMakeModelPlate,
            });

            const vehicleToDisplay = {
                licensePlateValue: newSelectedLicensePlate,
                value: 'DONTKNOW',
                label: newVehicleMakeModelPlate,
            };

            if (onVehicleChange) {
                onVehicleChange(vehicleToDisplay);
            }
        } else {
            const vehicleToDisplay = {
                licensePlateValue: newSelectedLicensePlate,
                value: newVehicleProfileId,
                label: newVehicleMakeModelPlate,
            };

            if (onVehicleChange) {
                onVehicleChange(vehicleToDisplay);
            }
        }

        if (onIDontKnowChanged) {
            onIDontKnowChanged(iDontKnowChecked);
        }

        if (
            !newVehicleProfileId &&
            !newVehicleInfoId &&
            !newUnlistedModel &&
            !iDontKnowChecked
        ) {
            this.setState({
                requiredError: this._requiredErrorMessage,
            });

            return false;
        }

        const vehicleToReturn = {};

        if (newVehicleProfileId) {
            vehicleToReturn.vehicleProfileId = parseInt(
                newVehicleProfileId,
                10
            );
        } else {
            vehicleToReturn.selectedLicensePlate = newSelectedLicensePlate;

            if (newVehicleInfoId) {
                vehicleToReturn.vehicleInfoId = parseInt(newVehicleInfoId, 10);
            } else if (newUnlistedModel) {
                vehicleToReturn.unlistedModel = newUnlistedModel;
            }
        }

        return vehicleToReturn;
    }

    render() {
        const {highlight, requiredError, vehicles} = this.state;
        const classes = classNames(
            'VehicleList',
            {'VehicleList-no-vehicles': vehicles.length === 1},
            {highlight},
            {ContainsErrors: requiredError}
        );

        return (
            <Fragment>
                <FormGroup className={classes}>
                    {this._renderVehicleList()}
                </FormGroup>
                {requiredError && (
                    <FormGroupError>{requiredError}</FormGroupError>
                )}
            </Fragment>
        );
    }
}

export default withConfig(['isMobile'])(VehicleList);
